import { checkIsLoop, checkIsLoopEnd } from "@/core/util";

export type RouterBranchLookup = ReadonlyMap<unknown, ReadonlySet<unknown>>;

/**
 * Own these lookups in diagram computeds. Reading the relevant fields here
 * tracks in-place edits; consumers need only constant-time lookups on render.
 */
export function createLoopNeighborLookup(
  steps: any[]
): ReadonlyMap<string, boolean> {
  const neighbors = new Map<string, boolean>();
  const visit = (list: any[]) => {
    if (!Array.isArray(list)) return;
    const loopFlags = list.map((step) => Boolean(checkIsLoop(step)));
    list.forEach((step, index) => {
      if (!step) return;
      // Match findStepByOrigId's depth-first, first-match order. Neighbors are
      // siblings in that list, not the nodes connected by diagram edges.
      if (!neighbors.has(step.origId)) {
        neighbors.set(
          step.origId,
          Boolean(loopFlags[index + 1] || loopFlags[index - 1])
        );
      }
      if (
        step.type &&
        step.type !== "LOOP" &&
        step.type !== "LOOP_END" &&
        Array.isArray(step.routes)
      ) {
        step.routes.forEach((route) => visit(route?.steps));
      }
    });
  };
  visit(steps);
  return neighbors;
}

export function createNextLoopEndLookup(
  nodes: any[],
  edges: any[]
): ReadonlyMap<string, boolean> {
  const loopEnds = new Map<string, boolean>();
  nodes.forEach((node) => {
    const id = node.data?.origId;
    if (!loopEnds.has(id)) loopEnds.set(id, checkIsLoopEnd(node.data));
  });
  const nextLoopEnds = new Map<string, boolean>();
  edges.forEach((edge) => {
    const { source, target, sourceHandle, hidden } = edge;
    // Unlike deletion eligibility, this rule ignores hidden edges. Retain a
    // false first result too: a later matching edge must not supersede it.
    if (
      sourceHandle?.includes("right") &&
      !hidden &&
      !nextLoopEnds.has(source)
    ) {
      nextLoopEnds.set(source, loopEnds.get(target) ?? false);
    }
  });
  return nextLoopEnds;
}

export function createRouterBranchLookup(nodes: any[]): RouterBranchLookup {
  const firstSteps = new Map<unknown, Set<unknown>>();
  nodes.forEach((node) => {
    if (node?.data?.type !== "ROUTER" || !Array.isArray(node.data.routes))
      return;
    node.data.routes.forEach((route) => {
      if (!Array.isArray(route.steps) || route.steps.length === 0) return;
      let steps = firstSteps.get(route.id);
      if (!steps) {
        steps = new Set();
        firstSteps.set(route.id, steps);
      }
      // These are UI IDs, not origIds. Separate keys preserve strict ID types
      // and avoid collisions from joining a route ID and step ID into a string.
      steps.add(route.steps[0]?.id);
    });
  });
  return firstSteps;
}

export function isStepInRouterBranch(
  step: any,
  nodes: any[],
  lookup?: RouterBranchLookup
): boolean {
  if (!step) return false;
  // Answer from the lookup before requiring `nodes`: a caller that has the
  // lookup does not need the array, and must not be silently told false.
  if (lookup) return lookup.get(step.parentId)?.has(step.id) ?? false;
  if (!nodes) return false;

  // Preserve the original search for CustomNode callers outside FlowDiagram.
  const routerNode = nodes.find((node: any) => {
    if (node?.data?.type !== "ROUTER" || !Array.isArray(node.data.routes))
      return false;
    return node.data.routes.some((route: any) => {
      if (route.id !== step.parentId) return false;
      if (!Array.isArray(route.steps) || route.steps.length === 0) return false;
      return route.steps[0]?.id === step.id;
    });
  });
  return !!routerNode;
}
